import { CircularProgress, styled } from "@mui/material";
import { ErrorBoundary } from "@appsignal/react";
import React, { useMemo } from "react";

import { LegacyWidgetConfiguration, Widget as WidgetType } from "@/lib/api";
import { WidgetTypes } from "@/types/widget";
import { isWidgetErrorResponse, useWidget } from "@/features/dashboard/features/widgets";
import { appSignal } from "@/appsignal";
import { useTranslation } from "@/lib/i18n";

import { LEGACY_WIDGETS, WIDGETS, WidgetComponent } from "./widget-registry";
import { WidgetError } from "./widget-error";

type Props = {
  widget: WidgetType;
};

/**
 * Thrown when a widget references a renderer that is not in the registry. Carries
 * its own type so the ErrorBoundary fallback can show a specific message while
 * still reporting the error to AppSignal.
 */
class UnknownWidgetRendererError extends Error {
  constructor(renderer: unknown) {
    super(`Unknown widget renderer: ${String(renderer)}`);
    this.name = "UnknownWidgetRendererError";
  }
}

const FallbackComponent = ({ error }: { error: Error }) => {
  const { t } = useTranslation();
  const message =
    error instanceof UnknownWidgetRendererError
      ? t("widgets.unknown_type_error")
      : t("widgets.load_error");
  return <WidgetError message={message} error={error} />;
};

const StyledLoadingContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const Widget = ({ widget }: Props) => {
  const { t } = useTranslation();
  const { configuration } = widget;

  const { data, status, fetchStatus, error } = useWidget(widget);

  const widgetOptions = useMemo(
    () => ({
      id: widget.id,
      title: widget.title,
      data: data?.data,
      configuration,
    }),
    [widget, configuration, data],
  );

  // The widget data request failed with an HTTP error (e.g. a legacy widget that
  // requires a location on a dashboard without any). The thrown error leaves
  // `data` undefined, so without this guard the request error would fall through
  // to the leaf renderer and surface a misleading body (e.g. the HTML widget's
  // "structured data" message). Show a clear load-error state instead.
  if (status === "error") {
    return <WidgetError message={t("widgets.load_error")} error={error} />;
  }

  if (fetchStatus === "fetching" && status !== "success") {
    return (
      <StyledLoadingContainer>
        <CircularProgress size={20} color="inherit" />
      </StyledLoadingContainer>
    );
  }

  return (
    <ErrorBoundary
      instance={appSignal}
      tags={{ component: "Widget", title: widget.title, widget: widget.id }}
      fallback={(error: Error) => <FallbackComponent error={error} />}
    >
      <WidgetRenderer widget={widget} widgetProps={widgetOptions} />
    </ErrorBoundary>
  );
};

export default Widget;

function isLegacyWidget(
  configuration: WidgetType["configuration"],
): configuration is LegacyWidgetConfiguration {
  return configuration?.type === WidgetTypes.LEGACY;
}

type WidgetRendererViewProps = {
  widget: WidgetType;
  widgetProps: Record<string, unknown>;
};

function WidgetRenderer({ widget, widgetProps }: WidgetRendererViewProps) {
  const isLegacy = isLegacyWidget(widget.configuration);
  const widgetMap = isLegacy ? LEGACY_WIDGETS : WIDGETS;
  const renderer = isLegacy
    ? (widget.configuration as LegacyWidgetConfiguration)?.renderer
    : widget.configuration?.type;

  if (!renderer || !(renderer in widgetMap)) {
    // Unknown widget type: throw so the ErrorBoundary reports it to AppSignal and
    // renders the shared error state, rather than silently showing an empty box.
    throw new UnknownWidgetRendererError(renderer);
  }

  if (isLegacy && isWidgetErrorResponse(widgetProps?.data)) {
    return <WidgetError key={widget.id} message={widgetProps.data.message} />;
  }

  const Resolved = widgetMap[renderer] as WidgetComponent;

  return React.createElement(Resolved, { key: widget.id, ...widget, ...widgetProps });
}
